// src/pages/auth/ResetPasswordPage.jsx

import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { resetPassword } from "../../services/authApi";
import logger from "../../utils/logger";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const evaluateStrength = (pwd) => {
    const hasLetter = /[A-Za-z]/.test(pwd);
    const hasDigit = /\d/.test(pwd);
    const hasSpecial = /[^A-Za-z0-9]/.test(pwd);

    if (pwd.length >= 12 && hasLetter && hasDigit && hasSpecial) return "robuste";
    if (pwd.length >= 8 && ((hasLetter && hasDigit) || (hasLetter && hasSpecial) || (hasDigit && hasSpecial))) return "fort";
    if (pwd.length >= 6) return "moyen";
    if (pwd.length > 0) return "faible";
    return "";
  };

  useEffect(() => {
    setStrength(evaluateStrength(password));
  }, [password]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (!token) {
      setError("Lien de réinitialisation invalide.");
      return;
    }

    if (!password || !confirmPassword) {
      setError("Veuillez remplir tous les champs.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await resetPassword(token, {
        new_password: password,
      });

      setMessage(data?.message || "Mot de passe réinitialisé avec succès.");
      setPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Lien invalide, expiré ou erreur serveur."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <ResetPanel>
        <PanelBadge>Compte utilisateur</PanelBadge>

        <h1>Réinitialiser mon mot de passe</h1>

        <p>
          Choisis un nouveau mot de passe pour finaliser l’activation de ton compte.
        </p>

        {message && <PanelMessage>{message}</PanelMessage>}
        {error && <PanelMessage $error>{error}</PanelMessage>}

        <Form onSubmit={handleSubmit}>
          <Field>
            <label>Nouveau mot de passe</label>

            <PasswordInput>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </PasswordInput>

            {strength && (
              <StrengthBlock>
                <StrengthTrack>
                  <StrengthFill $strength={strength} />
                </StrengthTrack>
                <StrengthLabel $strength={strength}>{strength}</StrengthLabel>
              </StrengthBlock>
            )}
          </Field>

          <Field>
            <label>Confirmer le mot de passe</label>

            <PasswordInput>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                required
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </PasswordInput>
          </Field>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? "Réinitialisation..." : "Réinitialiser"}
          </SubmitButton>
        </Form>
      </ResetPanel>
    </PageWrapper>
  );
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100vw;
  display: grid;
  place-items: center;
  padding: 1rem;
  box-sizing: border-box;
  background: #0b1328;
  overflow-y: auto;

  @media (max-height: 720px) {
    align-items: start;
  }
`;

const ResetPanel = styled.section`
  width: min(100%, 520px);
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  padding: 1.6rem;
  border-radius: 24px;
  background: #1d263d;
  color: #f7f7f7;
  box-sizing: border-box;

  h1 {
    margin: 0.8rem 0 0.5rem;
    font-size: clamp(1.35rem, 4vw, 1.65rem);
    font-weight: 950;
  }

  p {
    margin: 0 0 1.2rem;
    opacity: 0.74;
    line-height: 1.45;
  }
`;

const PanelBadge = styled.div`
  display: inline-block;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
`;

const Form = styled.form`
  display: grid;
  gap: 1rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;

  label {
    font-size: 0.8rem;
    font-weight: 850;
    opacity: 0.84;
  }
`;

const PasswordInput = styled.div`
  display: flex;
  align-items: center;
  background: #fff9ea;
  border-radius: 12px;
  overflow: hidden;

  input {
    flex: 1;
    min-height: 46px;
    border: none;
    padding: 0.75rem;
    background: transparent;
    color: #0b1328;
    font-weight: 750;
    outline: none;
  }

  button {
    width: 48px;
    height: 46px;
    border: none;
    background: transparent;
    color: #0b1328;
    cursor: pointer;
    display: grid;
    place-items: center;
  }
`;

const StrengthBlock = styled.div`
  display: grid;
  gap: 0.35rem;
  margin-top: 0.35rem;
`;

const StrengthTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 249, 234, 0.16);
  overflow: hidden;
`;

const StrengthFill = styled.div`
  height: 100%;
  width: ${({ $strength }) => {
    if ($strength === "robuste") return "100%";
    if ($strength === "fort") return "75%";
    if ($strength === "moyen") return "50%";
    return "25%";
  }};
  background: #fff9ea;
`;

const StrengthLabel = styled.span`
  font-size: 0.76rem;
  font-weight: 850;
  opacity: 0.8;
`;

const SubmitButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
  position: sticky;
  bottom: 0;
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;